import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";

/**
 * Step 3 — Design. Two-column layout:
 *   • Left: section sidebar listing every panel with status chips
 *     (ON-SPEC / DRIFT / OVERFLOW per content-drift, plus an OK ✓
 *     stamp once the marketer acknowledges the panel). Clicking a
 *     section opens the modal.
 *   • Right: the rendered PDF in an iframe. If the document hasn't
 *     been exported yet we show a clear placeholder.
 *   • Footer: the Finish button — consults the brand-template policy
 *     server-side; if requires_approval=false it goes straight to a
 *     clean PDF and routes to /my-documents; otherwise it submits for
 *     admin approval.
 */
export default function DesignStep({ document: doc, panelList, onChanged }) {
  const navigate = useNavigate();
  const [openPanelId, setOpenPanelId] = useState(null);
  const [busyPanelId, setBusyPanelId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [confirmResult, setConfirmResult] = useState(null);

  const acks = doc.section_acknowledgements || {};
  const totalPanels = panelList.length;
  const ackedPanels = panelList.filter((p) => acks[p.panel_id]).length;
  const allAcked = totalPanels > 0 && ackedPanels === totalPanels;

  const openPanel = panelList.find((p) => p.panel_id === openPanelId) || null;

  const acknowledge = async (panel_id) => {
    try {
      await api.post(`/document-versions/${doc.document_version_id}/panels/${panel_id}/acknowledge`);
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not acknowledge");
    }
  };

  const regenerate = async (panel_id, opts = {}) => {
    // Use a per-panel busy lock so the Finish button stays usable while
    // a single section regenerates.
    setBusyPanelId(panel_id); setError("");
    try {
      await api.post(
        `/document-versions/${doc.document_version_id}/panels/${panel_id}/regenerate`,
        opts,
      );
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not regenerate");
    } finally { setBusyPanelId(null); }
  };

  const finishDoc = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/document-versions/${doc.document_version_id}/finish`, {});
      setConfirmResult(data);
      onChanged?.();
      // Auto-route after a moment so the marketer can read the banner.
      setTimeout(() => {
        if (data.next_route) navigate(data.next_route);
      }, 1800);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not finish");
      setConfirmFinish(false);
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="design-step-root">
      {error && <p className="text-sm text-[#B5462F] mt-4">{error}</p>}
      {confirmResult && (
        <div data-testid="design-step-finish-banner" className="mt-4 border border-[#1F6B6B]/30 bg-[#1F6B6B]/10 rounded-lg p-4 text-sm text-[#1A1F2C]">
          {confirmResult.message}
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* SECTION SIDEBAR */}
        <aside data-testid="design-step-sidebar">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B6358] mb-2">
            Sections · {ackedPanels} / {totalPanels} acknowledged
          </p>
          <ul className="space-y-1.5">
            {panelList.map((p) => {
              const acked = !!acks[p.panel_id];
              const hasErr = p.warnings.some((w) => w.severity === "error");
              const hasWarn = p.warnings.some((w) => w.severity === "warning");
              const tone = hasErr ? "#B5462F" : hasWarn ? "#C28A2E" : "#1F6B6B";
              return (
                <li key={p.panel_id}>
                  <button
                    type="button"
                    data-testid={`design-step-section-${p.panel_id}`}
                    onClick={() => setOpenPanelId(p.panel_id)}
                    className="w-full text-left border border-[#1A1F2C]/10 bg-white rounded-md px-3 py-2.5 hover:border-[#1A1F2C]/30 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-[#1A1F2C]">
                        {p.label || p.panel_id}
                      </span>
                      <span
                        className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded"
                        style={{ backgroundColor: `${tone}1a`, color: tone }}
                      >
                        {hasErr ? "BLOCKER" : hasWarn ? "WARN" : "OK"}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-[#6B6358]">{p.elements.length} elements</span>
                      {acked && (
                        <span className="text-[10px] text-[#1F6B6B] font-medium" data-testid={`design-step-section-${p.panel_id}-ack`}>
                          ✓ Acknowledged
                        </span>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* PDF IFRAME */}
        <main>
          <div
            className="border border-[#1A1F2C]/10 bg-white rounded-lg overflow-hidden"
            style={{ height: "560px" }}
          >
            {doc.exported_pdf_url ? (
              <iframe
                data-testid="design-step-pdf"
                title="Document preview"
                src={doc.exported_pdf_url}
                className="w-full h-full"
              />
            ) : (
              <div
                data-testid="design-step-pdf-placeholder"
                className="h-full flex flex-col items-center justify-center text-center p-8 text-[#6B6358]"
              >
                <p className="font-serif text-xl text-[#1A1F2C]">No rendered PDF yet</p>
                <p className="text-sm mt-2 max-w-md">
                  The clean PDF is produced by Pica9 PDFMaker when you click <strong>Finish</strong>.
                  Use the section list to review each panel before publishing.
                </p>
              </div>
            )}
          </div>

          {/* FINISH FOOTER */}
          <div className="mt-5 flex flex-wrap gap-3 items-center justify-between border-t border-[#1A1F2C]/10 pt-5">
            <div className="text-xs text-[#6B6358] max-w-md">
              {allAcked
                ? "Every section acknowledged. Ready to finish."
                : `${totalPanels - ackedPanels} section${totalPanels - ackedPanels === 1 ? "" : "s"} not yet acknowledged. You can finish anyway.`}
            </div>
            <div className="flex gap-3">
              {!confirmFinish ? (
                <button
                  type="button"
                  data-testid="design-step-finish-button"
                  disabled={busy || !!confirmResult}
                  onClick={() => setConfirmFinish(true)}
                  className="px-6 py-2.5 bg-[#B5462F] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em] font-medium disabled:opacity-50"
                >
                  Finish design
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setConfirmFinish(false)}
                    disabled={busy}
                    className="px-4 py-2.5 text-xs uppercase tracking-[0.18em] text-[#6B6358]"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    data-testid="design-step-finish-confirm"
                    disabled={busy}
                    onClick={finishDoc}
                    className="px-6 py-2.5 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-xs uppercase tracking-[0.18em] font-medium disabled:opacity-50"
                  >
                    {busy ? "Working…" : "Confirm & publish"}
                  </button>
                </>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* SECTION MODAL */}
      {openPanel && (
        <SectionModal
          panel={openPanel}
          acked={!!acks[openPanel.panel_id]}
          onClose={() => setOpenPanelId(null)}
          onAcknowledge={() => acknowledge(openPanel.panel_id)}
          onRegenerate={(opts) => regenerate(openPanel.panel_id, opts)}
          busy={busyPanelId === openPanel.panel_id}
        />
      )}
    </div>
  );
}

function SectionModal({ panel, acked, onClose, onAcknowledge, onRegenerate, busy }) {
  // Pre-check the lock on every text slot the marketer has hand-edited
  // (current value drifts from baseline). They can still toggle.
  const initialLocks = {};
  panel.elements.forEach((el) => {
    if (el.kind !== "text") return;
    const edited = el.baseline_value != null && el.value !== el.baseline_value;
    if (edited) initialLocks[el.slot_id] = true;
  });
  const [locks, setLocks] = useState(initialLocks);
  const [hint, setHint] = useState("");

  // Diff-pulse: snapshot each slot's last_regenerated_at at modal open
  // (or after each successful regen). Any slot whose stamp is newer
  // than its snapshot gets a green-pulse animation for ~2.5s.
  const [openedAtStamps] = useState(() => {
    const map = {};
    panel.elements.forEach((el) => { map[el.slot_id] = el.last_regenerated_at || null; });
    return map;
  });
  const [pulseSet, setPulseSet] = useState(() => new Set());
  // When the panel prop refreshes after a regenerate, detect which slots
  // changed against the snapshot and trigger the pulse.
  const newlyChanged = panel.elements
    .filter((el) => {
      const before = openedAtStamps[el.slot_id];
      const now = el.last_regenerated_at;
      return now && now !== before && !pulseSet.has(el.slot_id);
    })
    .map((el) => el.slot_id);
  if (newlyChanged.length > 0) {
    // setState during render is fine here — we're computing a derived
    // set and only call the setter when the derived diff is non-empty.
    const next = new Set(pulseSet);
    newlyChanged.forEach((s) => next.add(s));
    setPulseSet(next);
    setTimeout(() => {
      setPulseSet((prev) => {
        const cleared = new Set(prev);
        newlyChanged.forEach((s) => cleared.delete(s));
        return cleared;
      });
    }, 2500);
  }

  const toggleLock = (slot_id) =>
    setLocks((prev) => ({ ...prev, [slot_id]: !prev[slot_id] }));

  const textSlots = panel.elements.filter((el) => el.kind === "text");
  const lockedCount = textSlots.filter((el) => locks[el.slot_id]).length;
  const allLocked = textSlots.length > 0 && lockedCount === textSlots.length;

  const fireRegenerate = () => {
    const preserve_slot_ids = textSlots
      .filter((el) => locks[el.slot_id])
      .map((el) => el.slot_id);
    onRegenerate({
      preserve_slot_ids,
      hint: hint.trim() || null,
    });
  };

  return (
    <div
      data-testid="design-step-section-modal"
      className="fixed inset-0 bg-[#1A1F2C]/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
    >
      <div
        className="bg-[#F8F5EE] rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-[#1A1F2C]/10 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#6B6358]">Section</p>
            <h2 className="font-serif text-2xl tracking-tight">{panel.label || panel.panel_id}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="design-step-section-modal-close"
            className="text-[#6B6358] text-2xl leading-none"
          >×</button>
        </div>

        <div className="px-6 py-5 space-y-3 text-sm">
          {panel.elements.map((el) => {
            const isTextSlot = el.kind === "text";
            const locked = !!locks[el.slot_id];
            const edited = isTextSlot && el.baseline_value != null && el.value !== el.baseline_value;
            const pulsing = pulseSet.has(el.slot_id);
            return (
              <div
                key={el.slot_id}
                data-testid={`design-step-section-modal-slot-${el.slot_id}`}
                className={[
                  "border rounded-md p-3 transition-colors duration-500",
                  pulsing
                    ? "border-[#1F6B6B] bg-[#1F6B6B]/10 ring-2 ring-[#1F6B6B]/30"
                    : "border-[#1A1F2C]/10 bg-white",
                ].join(" ")}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-[#6B6358]">
                    {el.kind} · {el.slot_id}
                  </span>
                  <div className="flex items-center gap-2">
                    {edited && (
                      <span className="text-[10px] text-[#C28A2E]">edited</span>
                    )}
                    {el.copy_source && (
                      <span className="text-[10px] text-[#3D3A35] italic">via {el.copy_source}</span>
                    )}
                    {isTextSlot && (
                      <label
                        className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-[#3D3A35] cursor-pointer select-none"
                        title="Lock to keep this slot's current wording during regenerate."
                      >
                        <input
                          type="checkbox"
                          data-testid={`design-step-section-modal-lock-${el.slot_id}`}
                          checked={locked}
                          onChange={() => toggleLock(el.slot_id)}
                          className="accent-[#1A1F2C]"
                        />
                        {locked ? "Locked" : "Lock"}
                      </label>
                    )}
                  </div>
                </div>
                <div className="mt-1 text-[#1A1F2C]">
                  {el.kind === "text" && (el.value || "—")}
                  {el.kind === "logo" && (el.asset_summary?.name || "Logo binding")}
                  {el.kind === "color_fill" && (
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: el.asset_summary?.hex || "#fff" }} />
                      {el.asset_summary?.hex || "(no color)"}
                    </span>
                  )}
                  {el.kind === "color_text" && (
                    <span className="inline-flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded" style={{ backgroundColor: el.asset_summary?.hex || "#000" }} />
                      {el.asset_summary?.hex || "(no color)"}
                    </span>
                  )}
                  {el.kind === "font" && (el.asset_summary?.family || "(no font)")}
                  {el.kind === "shape" && (el.asset_summary?.name || "Shape asset")}
                  {el.kind === "image" && (el.asset_summary?.alt || el.asset_summary?.url || "Image")}
                </div>
              </div>
            );
          })}
        </div>

        <div className="px-6 py-4 border-t border-[#1A1F2C]/10 bg-[#F8F5EE]/60">
          <label className="block text-[10px] uppercase tracking-[0.18em] text-[#6B6358] mb-1">
            Marketer guidance (optional) — steer the regenerate
          </label>
          <textarea
            data-testid="design-step-section-modal-hint"
            rows={2}
            value={hint}
            onChange={(e) => setHint(e.target.value)}
            placeholder='e.g. "make it shorter", "lead with the sail date", "drop the exclamation"'
            className="w-full px-3 py-2 text-sm border border-[#1A1F2C]/20 rounded-md bg-white"
          />
          {textSlots.length > 0 && (
            <p className="text-[10px] text-[#6B6358] mt-2">
              {lockedCount === 0 && "No slots locked — all text in this section will be regenerated."}
              {lockedCount > 0 && lockedCount < textSlots.length &&
                `${lockedCount} of ${textSlots.length} text slots locked — your edits will be preserved.`}
              {allLocked && "Every text slot is locked. Unlock at least one to regenerate."}
            </p>
          )}
        </div>

        <div className="px-6 py-4 border-t border-[#1A1F2C]/10 flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={fireRegenerate}
            data-testid="design-step-section-modal-regenerate"
            disabled={busy || allLocked || textSlots.length === 0}
            className="px-4 py-2 text-xs uppercase tracking-[0.18em] border border-[#1A1F2C]/30 rounded-full text-[#3D3A35] disabled:opacity-50"
          >
            {busy ? "Working…" : "Regenerate this section"}
          </button>
          <button
            type="button"
            onClick={onAcknowledge}
            data-testid="design-step-section-modal-ack"
            className={[
              "px-4 py-2 text-xs uppercase tracking-[0.18em] rounded-full font-medium",
              acked
                ? "bg-white text-[#3D3A35] border border-[#1A1F2C]/30"
                : "bg-[#1F6B6B] text-[#F8F5EE]",
            ].join(" ")}
          >
            {acked ? "Un-acknowledge" : "Acknowledge ✓"}
          </button>
        </div>
      </div>
    </div>
  );
}
